import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
  OnChanges,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { ConfigurationService } from '@sc/data-access-configuration';
import { TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { FocusManagerService } from '@sc/feature-focus-manager';
import {
  ButtonComponent,
  CheckboxFilterComponent,
  DatePickerComponent,
  IconComponent,
  InputTextComponent,
} from '@sc/ui-components';
import { dayjs } from '@sc/utils';
import {
  AppointmentType,
  DossiersSearchFilters,
  SearchFittersInput,
} from '../../models/dossiers-search-filters';
import { FilterDossiersStates } from '../../models/dossiers-states';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

@Component({
  selector: 'sc-dossiers-search-bar',
  styleUrls: ['./dossiers-search-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    InputTextComponent,
    ButtonComponent,
    NgIf,
    NgFor,
    DatePickerComponent,
    IconComponent,
    NgClass,
    MatMenuModule,
    TranslateModule,
    CheckboxFilterComponent,
    MatInputModule,
    MatSelectModule,
  ],
  template: `
    <div class="sc-design-title-h1 main-title">
      {{ 'DossiersSearchBar.searchTitle' | translate }}
    </div>
    <form *ngIf="form" [formGroup]="form" class="search-bar">
      <div class="sc-design-title-h2  secondary-title">
        {{ 'DossiersSearchBar.filterTitle' | translate }}
      </div>

      <div class="simpleSearch">
        <sc-input-text
          id="searchInput"
          class="input"
          formControlName="searchValue"
          [attr.aria-hidden]="showAdvancedSearch"
          [class.visible-div]="showAdvancedSearch"
          [label]="'DossiersSearchBar.searchInput' | translate"
          leftIcon="search"
          [forceShowLeftIcon]="true"
          [forceHighlightRequired]="false"
          [hint]="'DossiersSearchBar.searchInputHint' | translate"
        ></sc-input-text>

        <sc-button
          (click)="updateSearch()"
          [class.visible-div]="showAdvancedSearch"
          id="searchButton"
          [loading]="loading"
          [disabled]="loading"
        >
          {{ 'DossiersSearchBar.searchBtn' | translate }}
        </sc-button>

        <sc-button
          class="export-button"
          type="button"
          buttonType="secondary"
          leftIcon="export"
          [disabled]="!canExport"
          [class.visible-div]="showAdvancedSearch"
          (click)="onExportDossiers('excel')"
          >{{ 'DossiersSearchBar.btnExportExcel' | translate }}</sc-button
        >

        <sc-button
          type="button"
          buttonType="mainWhite"
          [rightIcon]="showAdvancedSearch ? 'arrow_top' : 'arrow_bottom'"
          (click)="toggleAdvancedSearch()"
          >{{ 'DossiersSearchBar.advancedSearch' | translate }}
        </sc-button>
      </div>

      <div *ngIf="showAdvancedSearch" class="search-bar-body-advanced">
        <div class="search-bar-filters-bloc">
          <div>
            <div class="w-50">
              <sc-input-text
                id="immatInput"
                [forceHighlightRequired]="false"
                class="input mb-l"
                formControlName="licensePlate"
                [label]="'DossiersSearchBar.filterLicensePlate' | translate"
              ></sc-input-text>
            </div>
            <div class="w-50">
              <sc-input-text
                id="dossierNumInput"
                class="input mb-l"
                [forceHighlightRequired]="false"
                formControlName="dossierNum"
                [label]="'DossiersSearchBar.filterDossierNum' | translate"
              ></sc-input-text>
            </div>
          </div>
          <div>
            <div class="w-50">
              <sc-input-text
                id="lastNameInput"
                class="input mb-l"
                [forceHighlightRequired]="false"
                formControlName="lastName"
                [label]="'DossiersSearchBar.filterLastName' | translate"
              ></sc-input-text>
            </div>
            <div class="w-50">
              <sc-input-text
                id="firstNameInput"
                class="input mb-l"
                [forceHighlightRequired]="false"
                formControlName="firstName"
                [label]="'DossiersSearchBar.filterFirstName' | translate"
              ></sc-input-text>
            </div>
          </div>
          <div>
            <div class="w-50">
              <label class="mb sc-design-text-4">{{
                'DossiersSearchBar.filterAppointmentType' | translate
              }}</label>
              <mat-form-field
                class="center-input no-hint"
                [appearance]="matAppearance"
              >
                <mat-select formControlName="appointmentType" multiple>
                  <mat-select-trigger>
                    {{
                      getAppointmentTypeDisplay(
                        form.get('appointmentType').value
                      )
                    }}
                  </mat-select-trigger>
                  <mat-option
                    *ngFor="let type of appointmentTypes"
                    [value]="type"
                    [disabled]="
                      type === 'OUTSIDE' && displayOnlyRequestedFilters
                    "
                  >
                    {{ getAppointmentTypeDisplay([type]) }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
            <div class="w-50" *ngIf="!displayOnlyRequestedFilters">
              <label class="mb sc-design-text-4">{{
                'DossiersSearchBar.steps' | translate
              }}</label>
              <mat-form-field
                class="step-input no-hint"
                [appearance]="matAppearance"
              >
                <mat-select formControlName="step" multiple>
                  <mat-select-trigger>
                    {{ getStepDisplay(form.get('step').value) }}
                  </mat-select-trigger>
                  <mat-option
                    *ngFor="let step of selectableSteps"
                    [value]="step"
                  >
                    {{ getStepDisplay([step]) }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>
          <div>
            <div class="w-50" *ngIf="!displayOnlyRequestedFilters">
              <label class="mb sc-design-text-4">{{
                'DossiersSearchBar.filterOrigin' | translate
              }}</label>
              <mat-form-field
                class="resource-input no-hint"
                [appearance]="matAppearance"
              >
                <mat-select formControlName="dossierGlobalOrigins" multiple>
                  <mat-select-trigger>
                    {{ getOrigin(form.get('dossierGlobalOrigins').value) }}
                  </mat-select-trigger>
                  <mat-option
                    *ngFor="let dossierGlobalOrigin of dossierGlobalOrigins"
                    [value]="dossierGlobalOrigin"
                  >
                    {{ getOrigin([dossierGlobalOrigin]) }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
            <div class="w-50" *ngIf="!displayOnlyRequestedFilters">
              <label class="mb sc-design-text-4">{{
                'DossiersSearchBar.filterFitter' | translate
              }}</label>
              <mat-form-field
                class="resource-input no-hint"
                [appearance]="matAppearance"
              >
                <mat-select formControlName="fitterBrands" multiple>
                  <mat-option
                    *ngFor="let fitterBrand of fitterBrands"
                    [value]="fitterBrand"
                  >
                    {{ fitterBrand.name }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
          </div>
          <div>
            <sc-date-picker
              class="date-picker"
              [forceHighlightRequired]="false"
              formControlName="startDate"
              [label]="'DossiersSearchBar.filterStartDate' | translate"
              [maxDate]="form.get('endDate')?.value"
            ></sc-date-picker>

            <span class="icon-between-date-pickers">
              <sc-icon class="date-separator" name="arrow_right"></sc-icon>
            </span>

            <sc-date-picker
              class="date-picker"
              [forceHighlightRequired]="false"
              formControlName="endDate"
              [label]="'DossiersSearchBar.filterEndDate' | translate"
              [minDate]="form.get('startDate')?.value"
            ></sc-date-picker>
          </div>
        </div>
        <div class="search-bar-buttons-bloc">
          <sc-button
            class="loadingButton"
            type="button"
            [disabled]="loading"
            [loading]="loading"
            (click)="updateSearch()"
            >{{ 'DossiersSearchBar.searchBtn' | translate }}</sc-button
          >
          <sc-button
            class="searchButton"
            type="button"
            buttonType="errorWhite"
            (click)="clearFilters()"
            >{{ 'DossiersSearchBar.btnClearFilters' | translate }}</sc-button
          >
          <sc-button
            class="exportButton"
            type="button"
            buttonType="secondary"
            leftIcon="export"
            [disabled]="!canExport"
            (click)="onExportDossiers('excel')"
            >{{ 'DossiersSearchBar.btnExportExcel' | translate }}</sc-button
          >
        </div>
      </div>
    </form>
    <div
      *ngIf="form"
      class="state-filters-container"
      [ngClass]="{ 'only-requested-filters': displayOnlyRequestedFilters }"
    >
      <div class="select-all-container" *ngIf="!displayOnlyRequestedFilters">
        <sc-button
          type="button"
          buttonType="tertiary"
          rightIcon="arrow_bottom"
          class="select-all-btn-menu"
          [matMenuTriggerFor]="menu"
          (menuOpened)="onSelectAllOpen()"
          (menuClosed)="onSelectAllClose()"
        >
          {{ 'DossiersSearchBar.filtersBtn' | translate }}
        </sc-button>
        <mat-menu class="menu-group-filter" #menu="matMenu">
          <div class="menu-group-filter-container" mat-menu-content>
            <button mat-menu-item (click)="onSelectAllRadioChange('all')">
              {{ 'DossiersSearchBar.Filters.all' | translate }}
            </button>
            <button mat-menu-item (click)="onSelectAllRadioChange('none')">
              {{ 'DossiersSearchBar.Filters.none' | translate }}
            </button>
          </div>
        </mat-menu>
      </div>
      <sc-checkbox-filter
        *ngIf="!displayOnlyRequestedFilters"
        type="warn"
        [toggleValue]="form?.get('stateInProgress').value"
        [text]="'DossiersSearchBar.dossierState.inProgress' | translate"
        (toggleEvent)="updateDossiersStatesActive('inProgress')"
      ></sc-checkbox-filter>
      <sc-checkbox-filter
        *ngIf="!displayOnlyRequestedFilters"
        type="success"
        [toggleValue]="form?.get('stateComplete').value"
        [text]="'DossiersSearchBar.dossierState.complete' | translate"
        (toggleEvent)="updateDossiersStatesActive('complete')"
      ></sc-checkbox-filter>
      <sc-checkbox-filter
        *ngIf="canHaveAppointmentCancel()"
        type="error"
        [toggleValue]="form?.get('stateCancelled').value"
        [text]="'DossiersSearchBar.dossierState.cancelled' | translate"
        (toggleEvent)="updateDossiersStatesActive('cancelled')"
      ></sc-checkbox-filter>
      <sc-checkbox-filter
        *ngIf="!displayOnlyRequestedFilters && canAbandonDossier()"
        type="error-alt-1"
        [toggleValue]="form?.get('stateAbandoned').value"
        [text]="'DossiersSearchBar.dossierState.abandoned' | translate"
        (toggleEvent)="updateDossiersStatesActive('abandoned')"
      ></sc-checkbox-filter>
      <sc-checkbox-filter
        *ngIf="displayRequestedFilters"
        type="requested"
        [toggleValue]="form?.get('stateRequested').value"
        [text]="
          displayOnlyRequestedFilters
            ? ('DossiersSearchBar.dossierState.requested' | translate)
            : ('DossiersSearchBar.dossierState.requestedGeneral' | translate)
        "
        (toggleEvent)="updateDossiersStatesActive('stateRequested')"
      ></sc-checkbox-filter>
      <sc-checkbox-filter
        *ngIf="hasValidatedRequestApts"
        type="requestedAndValidated"
        [toggleValue]="form?.get('stateRequestedAndValidated').value"
        [text]="
          'DossiersSearchBar.dossierState.requestedAndValidated' | translate
        "
        (toggleEvent)="updateDossiersStatesActive('stateRequestedAndValidated')"
      ></sc-checkbox-filter>
    </div>
  `,
})
export class DossiersSearchBarComponent implements OnInit, OnChanges {
  @Input() initValue: string;
  @Input() loading = false;
  @Input() dossiersStates: FilterDossiersStates;
  @Input() displayRequestedFilters = false;
  @Input() displayOnlyRequestedFilters = false;
  @Input() hasValidatedRequestApts = false;
  @Input() canExport: boolean;
  @Output() searchEvent = new EventEmitter<{
    searchText: string;
    searchCriteria?: DossiersSearchFilters;
    creationDates: { start: string; end: string };
    fitters: SearchFittersInput[];
    dossiersStates: FilterDossiersStates;
  }>();

  @Output() exportDossiers = new EventEmitter<'excel' | 'pdf'>();
  form: FormGroup;
  dossierGlobalOrigins;
  fitterBrands;
  showAdvancedSearch = false;
  appointmentTypes: AppointmentType[] = ['OUTSIDE', 'IN_CENTER'];
  selectableSteps: string[];

  matAppearance: MatFormFieldAppearance;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly translator: TranslatorService,
    private readonly configurationService: ConfigurationService,
    private readonly focusManager: FocusManagerService,
    private readonly featureFlipService: FeatureFlippingManagerService
  ) {
    this.matAppearance =
      this.configurationService.getConfig()?.themeOptions?.matInputAppearance;
  }

  ngOnInit(): void {
    this.dossierGlobalOrigins =
      this.configurationService.getOriginFiltersAllowed();
    this.fitterBrands = this.configurationService.getFitterBrands();
    this.selectableSteps = this.configurationService
      ?.getConfig()
      ?.stepperPageMapping?.map((step) => step?.stepKey);

    this.form = new FormGroup({
      searchValue: new FormControl(this.initValue || ''),
      licensePlate: new FormControl(),
      lastName: new FormControl(),
      firstName: new FormControl(),
      dossierNum: new FormControl(),
      startDate: new FormControl(),
      endDate: new FormControl(),
      appointmentType: new FormControl(),
      dossierGlobalOrigins: new FormControl(),
      fitterBrands: new FormControl(),
      step: new FormControl(),
      stateComplete: new FormControl(
        this.displayOnlyRequestedFilters ? false : this.dossiersStates.complete
      ),
      stateInProgress: new FormControl(
        this.displayOnlyRequestedFilters
          ? false
          : this.dossiersStates.inProgress
      ),
      stateCancelled: new FormControl(
        this.displayOnlyRequestedFilters ? false : this.dossiersStates.cancelled
      ),
      stateAbandoned: new FormControl(
        this.displayOnlyRequestedFilters || !this.canAbandonDossier()
          ? false
          : this.dossiersStates.abandoned
      ),
      stateRequested: new FormControl(this.dossiersStates?.requested),
      stateRequestedAndValidated: new FormControl(
        this.dossiersStates?.requestedAndValidated
      ),
    });
  }

  ngOnChanges() {
    this.changeDetectorRef.detectChanges();
  }

  onSelectAllOpen() {
    this.focusManager.focusFirstElementAllowed(
      '.menu-group-filter .menu-group-filter-container'
    );
  }

  onSelectAllClose() {
    this.focusManager.focusFirstElementAllowed(
      'sc-dossiers-search-bar .select-all-btn-menu',
      true
    );
  }

  onSelectAllRadioChange(newValue: 'all' | 'none') {
    if (newValue === 'all') {
      this.form.patchValue({
        stateInProgress: true,
        stateComplete: true,
        stateCancelled: true,
        stateAbandoned: true,
        stateRequested: true,
        stateRequestedAndValidated: true,
      });
    } else if (newValue === 'none') {
      this.form.patchValue({
        stateInProgress: false,
        stateComplete: false,
        stateCancelled: false,
        stateAbandoned: false,
        stateRequested: false,
        stateRequestedAndValidated: false,
      });
    }

    this.updateSearch();
  }

  getOrigin(dossierGlobalOrigins: string[]): string[] {
    return dossierGlobalOrigins?.map((origin) =>
      this.translator.getInstantTranslation(
        `GlobalApplicativeConstants.DossierOrigins.${origin}`
      )
    );
  }

  updateDossiersStatesActive(dossierState) {
    const form = this.form.getRawValue();

    switch (dossierState) {
      case 'inProgress':
        this.form.patchValue({
          stateInProgress: !form.stateInProgress,
        });
        break;
      case 'complete':
        this.form.patchValue({
          stateComplete: !form.stateComplete,
        });
        break;
      case 'cancelled':
        this.form.patchValue({
          stateCancelled:
            this.canHaveAppointmentCancel() && !form.stateCancelled,
        });
        break;
      case 'abandoned':
        this.form.patchValue({
          stateAbandoned: !form.stateAbandoned,
        });
        break;
      case 'stateRequested':
        this.form.patchValue({
          stateRequested: !form.stateRequested,
        });
        break;
      case 'stateRequestedAndValidated':
        this.form.patchValue({
          stateRequestedAndValidated: !form.stateRequestedAndValidated,
        });
        break;
    }
    this.updateSearch();
  }

  updateSearch() {
    const startDate = dayjs(this.form.get('startDate')?.value);
    const endDate = dayjs(this.form.get('endDate')?.value);
    const fitters: SearchFittersInput[] = this.form
      .get('fitterBrands')
      ?.value?.map((brand) => ({ fitterId: null, brandId: brand.id }));

    this.searchEvent.emit({
      searchText: this.form.get('searchValue')?.value,
      searchCriteria: {
        ...this.getSelectedAdvancedFilters(),
      },
      creationDates: {
        start: startDate.isValid() ? startDate.format('YYYY-MM-DD') : null,
        end: endDate.isValid() ? endDate?.format('YYYY-MM-DD') : null,
      },
      fitters,
      dossiersStates: {
        complete: this?.displayOnlyRequestedFilters
          ? false
          : this.form.get('stateComplete')?.value,
        inProgress: this?.displayOnlyRequestedFilters
          ? false
          : this.form.get('stateInProgress')?.value,
        cancelled: this?.displayOnlyRequestedFilters
          ? false
          : this.form.get('stateCancelled')?.value,
        abandoned:
          this.displayOnlyRequestedFilters || !this.canAbandonDossier()
            ? false
            : this.form.get('stateAbandoned')?.value,
        requested: !this.displayRequestedFilters
          ? false
          : this.form.get('stateRequested')?.value,
        requestedAndValidated: this?.hasValidatedRequestApts
          ? this.form.get('stateRequestedAndValidated')?.value
          : false,
      },
    });
    this.changeDetectorRef.detectChanges();
  }

  toggleAdvancedSearch() {
    this.showAdvancedSearch = !this.showAdvancedSearch;
  }

  getSelectedAdvancedFilters(): Partial<DossiersSearchFilters> {
    const formValues = this.form.getRawValue();

    if (this.showAdvancedSearch) {
      return {
        licensePlate: formValues.licensePlate || null,
        lastName: formValues.lastName || null,
        firstName: formValues.firstName || null,
        dossierNum: formValues.dossierNum || null,
        step: formValues.step || null,
        appointmentType: formValues.appointmentType || null,
        dossierGlobalOrigins: formValues?.dossierGlobalOrigins || null,
      };
    } else {
      return {};
    }
  }

  getAppointmentTypeDisplay(appointmentTypes: AppointmentType[]) {
    return appointmentTypes?.map((appointmentType) =>
      this.translator.getInstantTranslation(
        `DossiersSearchBar.appointmentTypes.${appointmentType}`
      )
    );
  }

  getStepDisplay(steps: string[]) {
    // NOTE: adapt traductions to b2b based on origin selection, when needed
    return steps?.map((step) =>
      this.translator.getInstantTranslation(
        `GlobalApplicativeConstants.StepperTitles.CustomerStepper.${step}`
      )
    );
  }

  onExportDossiers(format: 'excel' | 'pdf') {
    this.exportDossiers.emit(format);
  }

  clearFilters() {
    Object.keys(this.form.controls).forEach((controlName) => {
      if (!controlName?.includes('state')) {
        this.form.controls[controlName].setValue(null);
      }
    });
    this.updateSearch();
  }

  canHaveAppointmentCancel() {
    return (
      !this.displayOnlyRequestedFilters &&
      this.featureFlipService.isFeatureActivated(
        'can-do-appointment-cancel-insurer'
      ) &&
      this.featureFlipService.brandFeatureActivatedExists(
        'can-do-appointment-cancel-fitter'
      )
    );
  }

  canAbandonDossier() {
    return this.featureFlipService.isFeatureActivated(
      'b2b-allow-abandon-files'
    );
  }
}
